// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Props:
 *  * navigationOutSide: To add arrows outside swiper container, to fix overflow issue.
 *  * xController: To create custom controller.
 *  * extendedStyles: to append style to the component
 */

import SwiperCore, { A11y, Autoplay, Controller, EffectFade, Navigation, Pagination, Scrollbar } from "swiper";
import { Swiper, SwiperProps, SwiperSlide } from "swiper/react";

import ArrowNavigation from "@conklin/App/Components/XngCarousel/ArrowNavigation";
import { xSwiperGreyShadow, xSwiperShadow, xSwiperStyle } from "@conklin/App/Components/XngCarousel/styles";
import { color } from "@conklin/XDL/Utilities";
import { mergeWithCss } from "@conklin/XDL/Utilities/mergeWithCss";

import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import pick from "lodash/pick";
import React, { FC, isValidElement, useEffect, useState } from "react";
import { css } from "styled-components";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Controller, Autoplay, EffectFade]);

export type Props = SwiperProps & {
    extendedStyles?: SwiperStyle;
    navigationOutSide?: boolean;
    showMobileNavigation?: boolean;
    arrowNavigationType?: "default" | "circle";
};

export interface SwiperStyle {
    container?: InjectableCss;
}

// project style
const swiperStyle: SwiperStyle = {
    container: {
        css: css`
            ${xSwiperStyle}

            .xng-pagination-container {
                &:not(.visible) {
                    display: none;
                }
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 16px;
                gap: 12px;
                & > div,
                & > button {
                    margin: 0;
                }
            }

            .xng-pagination-container > .swiper-scrollbar.swiper-scrollbar-horizontal {
                height: 8px;
                width: 100%;
                position: relative;
                top: unset;
                right: unset;
                bottom: unset;
                left: unset;
                transform: initial;
                display: flex;
                align-items: center;
                background: transparent;
                border-radius: 4px;
                overflow: hidden;
                & > .swiper-scrollbar-drag {
                    background: ${color("common.ui5")};
                    border-radius: 4px;
                    height: 4px;
                    cursor: pointer;
                }
            }

            .swiper-horizontal > .xng-pagination-container {
                display: flex !important;
                width: 100%;
                align-items: center;
                padding: 0;
                border-radius: 0;
                background: transparent;
            }

            .swiper-pagination.swiper-pagination-bullets {
                position: relative;
                text-align: center;
                display: flex;
                gap: 4px;
                border: none;
                ${({ mobileStyle }: any) =>
                    mobileStyle
                        ? css`
                              justify-content: center;
                          `
                        : css`
                              width: 100%;
                          `}
                &.swiper-pagination-lock {
                    display: none;
                }

                .swiper-pagination-bullet.swiper-pagination-bullet {
                    display: flex;
                    flex: 1;
                    ${({ mobileStyle }: any) =>
                        mobileStyle
                            ? css`
                                  height: 10px;
                                  width: 10px;
                                  border-radius: 50%;
                                  border: 1px solid ${color("common.ui4")};
                                  background: transparent;
                              `
                            : css`
                                  height: 4px;
                                  border-radius: 0;
                                  background: ${color("common.ui4")};
                              `}
                    opacity: 1;
                    box-sizing: border-box;
                    margin: 0;
                    &.swiper-pagination-bullet-active {
                        background: ${color("common.ui10")};
                    }
                    &.dots-with-numbers {
                        padding: 10px;
                        height: unset;
                        width: unset;
                        align-items: center;
                        justify-content: center;
                        font-size: 13px;
                        font-weight: ${fontConfig.weight.bold};
                        color: ${color("common.black")};
                        text-align: center;
                    }
                    &:hover {
                        background: ${color("common.ui10")} !important;
                    }
                }
            }
            ${({ isAuto }: any) =>
                isAuto
                    ? css`
                          .swiper-slide {
                              width: auto;
                              overflow: hidden;
                          }
                      `
                    : ""}

            ${xSwiperShadow}
            ${xSwiperGreyShadow}
        `,
    },
};

const XngCarousel = (props: Props) => {
    const {
        children,
        className,
        extendedStyles,
        navigationOutSide,
        showMobileNavigation,
        arrowNavigationType = "default",
        ...swiperProps
    } = props;
    const [isSwiperCssImported, setIsSwiperCssImported] = useState(false);
    const [styles] = useState(() => mergeWithCss(swiperStyle, extendedStyles));

    useEffect(() => {
        const getStyle = async () => {
            /* eslint-disable global-require */
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            await require("swiper/swiper-bundle.css");
            /* eslint-enable global-require */
        };
        getStyle().then(_ => setIsSwiperCssImported(true));
    }, []);

    if (!isSwiperCssImported) {
        return null;
    }

    let paginationOptions;
    let scrollbarOptions;

    if (swiperProps.pagination) {
        paginationOptions =
            typeof swiperProps.pagination === "boolean"
                ? { el: ".swiper-pagination", clickable: true }
                : swiperProps.pagination;
    }

    if (swiperProps.scrollbar) {
        scrollbarOptions =
            typeof swiperProps.scrollbar === "boolean"
                ? { draggable: true, el: ".swiper-scrollbar" }
                : swiperProps.scrollbar;
    }

    return (
        <StyledWrapper
            {...styles.container}
            className={`carousel-main-wrapper ${className || ""}`}
            {...{ mobileStyle: showMobileNavigation, isAuto: props.slidesPerView === "auto" && !props.breakpoints }}
        >
            {/* Add observer/observeParents in-order to re-init in case of content change  */}
            <Swiper
                {...swiperProps}
                pagination={paginationOptions}
                scrollbar={scrollbarOptions}
                observeParents
                observer
            >
                {React.Children.map(children, child => {
                    // for null case
                    if (!isValidElement(child)) {
                        return;
                    }

                    const { props } = child;
                    const swiperSlidePropsKeys = ["tag", "zoom", "virtualIndex"];
                    const childrenProps = omitMultiple(props as any, [...swiperSlidePropsKeys]);
                    const swiperSlideProps = pick(props, swiperSlidePropsKeys);

                    return (
                        <SwiperSlide {...swiperSlideProps}>
                            {React.createElement(child.type, childrenProps)}
                        </SwiperSlide>
                    );
                })}

                {swiperProps.navigation && navigationOutSide && !showMobileNavigation && (
                    <>
                        <ArrowNavigation
                            showOnMobile={false}
                            arrowType="prev"
                            arrowNavigationType={arrowNavigationType}
                            scrollbar={swiperProps.scrollbar}
                        />
                        <ArrowNavigation
                            showOnMobile={false}
                            arrowType="next"
                            arrowNavigationType={arrowNavigationType}
                            scrollbar={swiperProps.scrollbar}
                        />
                    </>
                )}

                {(swiperProps.pagination ||
                    (swiperProps.navigation && showMobileNavigation) ||
                    swiperProps.scrollbar) && (
                    <StyledWrapper
                        className={`xng-pagination-container  ${showMobileNavigation ? "mobile-style" : ""}`}
                    >
                        {swiperProps.navigation && showMobileNavigation && (
                            <ArrowNavigation
                                arrowType="prev"
                                arrowNavigationType={arrowNavigationType}
                                showOnMobile
                                scrollbar={swiperProps.scrollbar}
                            />
                        )}
                        {swiperProps.pagination && <div className="swiper-pagination" />}
                        {swiperProps.scrollbar && <div className="swiper-scrollbar" />}
                        {swiperProps.navigation && showMobileNavigation && (
                            <ArrowNavigation
                                arrowType="next"
                                arrowNavigationType={arrowNavigationType}
                                showOnMobile
                                scrollbar={swiperProps.scrollbar}
                            />
                        )}
                    </StyledWrapper>
                )}
            </Swiper>
        </StyledWrapper>
    );
};

XngCarousel.defaultProps = {
    slidesPerView: "auto",
    navigation: true,
    simulateTouch: false,
    spaceBetween: 20,
    pagination: false,
    scrollbar: false,
    watchOverflow: true,
    updateOnWindowResize: true,
    centerInsufficientSlides: true,
    draggable: true,
    freeMode: false,
    navigationOutSide: true,
    fadeEffect: {
        crossFade: true,
    },
};

export default XngCarousel;
